import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import { reduxForm, Fields } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import Button from 'components/common/Button';
import DatepickerField from 'components/fields/DatepickerField';

import * as normalize from 'utils/normalize';
import * as validate from 'utils/validate';

import dateFormat from 'config/dateFormat';

const styles = theme => ({
  section: {
    '& + $section': {
      borderTop: `1px solid ${theme.palette.primary.A100}`,
    },
  },
  sectionHeader: {
    padding: '16px 16px 0',
  },
  sectionBody: {
    padding: 16,
  },
  gridDateRange: {
    marginBottom: 4,
  },
  buttonSubmit: {
    minWidth: 0,
  },
  buttonCancel: {
    fontWeight: 400,
    textTransform: 'none',
    minWidth: 0,
    color: theme.palette.grey[700],
  },
});

const validateForm = (values) => {
  const errors = {};

  if (values['Date of Creation From']) {
    if (validate.date(values['Date of Creation From'])) {
      errors['Date of Creation From'] = validate.date(values['Date of Creation From']);
    }
    if (!values['Date of Creation To']) {
      errors['Date of Creation To'] = 'Field is required if Date of Creation From is selected';
    }
  }

  if (values['Date of Creation To']) {
    if (validate.date(values['Date of Creation To'])) {
      errors['Date of Creation To'] = validate.date(values['Date of Creation To']);
    }
    if (!values['Date of Creation From']) {
      errors['Date of Creation From'] = 'Field is required if Date of Creation To is selected';
    }
  }

  if (values['Date of Creation From']
    && !validate.date(values['Date of Creation From'])
    && values['Date of Creation To']
    && !validate.date(values['Date of Creation To'])
    && moment(values['Date of Creation From'], dateFormat)
      .isAfter(moment(values['Date of Creation To'], dateFormat))) {
    errors['Date of Creation From'] = 'Should be before Date of Creation To';
  }

  if (values['Date of Service From']) {
    if (validate.date(values['Date of Service From'])) {
      errors['Date of Service From'] = validate.date(values['Date of Service From']);
    }
    if (!values['Date of Service To']) {
      errors['Date of Service To'] = 'Field is required if Date of Service From is selected';
    }
  }

  if (values['Date of Service To']) {
    if (validate.date(values['Date of Service To'])) {
      errors['Date of Service To'] = validate.date(values['Date of Service To']);
    }
    if (!values['Date of Service From']) {
      errors['Date of Service From'] = 'Field is required if Date of Service To is selected';
    }
  }

  if (values['Date of Service From']
    && !validate.date(values['Date of Service From'])
    && values['Date of Service To']
    && !validate.date(values['Date of Service To'])
    && moment(values['Date of Service From'], dateFormat)
      .isAfter(moment(values['Date of Service To'], dateFormat))) {
    errors['Date of Service From'] = 'Should be before Date of Service To';
  }

  if (values['Date of Delivery From']) {
    if (validate.date(values['Date of Delivery From'])) {
      errors['Date of Delivery From'] = validate.date(values['Date of Delivery From']);
    }
    if (!values['Date of Delivery To']) {
      errors['Date of Delivery To'] = 'Field is required if Date of Delivery From is selected';
    }
  }

  if (values['Date of Delivery To']) {
    if (validate.date(values['Date of Delivery To'])) {
      errors['Date of Delivery To'] = validate.date(values['Date of Delivery To']);
    }
    if (!values['Date of Delivery From']) {
      errors['Date of Delivery From'] = 'Field is required if Date of Delivery To is selected';
    }
  }

  if (values['Date of Delivery From']
    && !validate.date(values['Date of Delivery From'])
    && values['Date of Delivery To']
    && !validate.date(values['Date of Delivery To'])
    && moment(values['Date of Delivery From'], dateFormat)
      .isAfter(moment(values['Date of Delivery To'], dateFormat))) {
    errors['Date of Delivery From'] = 'Should be before Date of Delivery To';
  }

  return errors;
};

const DateRangeFieldsForm = ({
  submit,
  onClear,
  dateOfCreation,
  dateOfService,
  dateOfDelivery,
  dateOfClaimSubmission,
  datepickerPlacement,
  dateOfCreationLabel,
  classes,
}) => (
  <Fragment>
    {dateOfService &&
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography>
            Date of Service
          </Typography>
        </div>
        <div className={classes.sectionBody}>
          <Grid
            container
            justify="flex-start"
            alignItems="flex-end"
            wrap="nowrap"
            spacing={8}
          >
            <Grid
              item
              xs={12}
            >
              <Fields
                id="Date of Service From/To"
                names={[
                  'Date of Service From',
                  'Date of Service To',
                ]}
                component={DatepickerField}
                normalize={{
                  'Date of Service From': normalize.date,
                  'Date of Service To': normalize.date,
                }}
                size="sm"
                fullWidth
                minDate="01/01/1900"
                maxDate="01/01/2100"
                dateFormat={dateFormat}
                placeholder="mm/dd/yyyy"
                variant="range"
                startDateName="Date of Service From"
                endDateName="Date of Service To"
                rangeButtons
              />
            </Grid>
          </Grid>
        </div>
      </div>
    }
    {dateOfDelivery &&
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography>
            Date of Delivery
          </Typography>
        </div>
        <div className={classes.sectionBody}>
          <Grid
            container
            justify="flex-start"
            alignItems="flex-end"
            wrap="nowrap"
            spacing={8}
          >
            <Grid
              item
              xs={12}
            >
              <Fields
                id="Date of Delivery From/To"
                names={[
                  'Date of Delivery From',
                  'Date of Delivery To',
                ]}
                component={DatepickerField}
                normalize={{
                  'Date of Delivery From': normalize.date,
                  'Date of Delivery To': normalize.date,
                }}
                size="sm"
                fullWidth
                minDate="01/01/1900"
                maxDate="01/01/2100"
                dateFormat={dateFormat}
                placeholder="mm/dd/yyyy"
                variant="range"
                startDateName="Date of Delivery From"
                endDateName="Date of Delivery To"
                rangeButtons
              />
            </Grid>
          </Grid>
        </div>
      </div>
    }
    {dateOfCreation &&
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography>
            {dateOfCreationLabel || 'Date of Creation'}
          </Typography>
        </div>
        <div className={classes.sectionBody}>
          <Grid
            container
            justify="flex-start"
            alignItems="flex-end"
            spacing={8}
          >
            <Grid
              item
              xs={12}
            >
              <Fields
                id="Date of Creation From/To"
                names={[
                  'Date of Creation From',
                  'Date of Creation To',
                ]}
                component={DatepickerField}
                normalize={{
                  'Date of Creation From': normalize.date,
                  'Date of Creation To': normalize.date,
                }}
                fullWidth
                size="sm"
                minDate="01/01/1900"
                maxDate="01/01/2100"
                dateFormat={dateFormat}
                placeholder="mm/dd/yyyy"
                variant="range"
                startDateName="Date of Creation From"
                endDateName="Date of Creation To"
                datepickerPlacement={datepickerPlacement}
                rangeButtons
              />
            </Grid>
          </Grid>
        </div>
      </div>
    }
    {dateOfClaimSubmission &&
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography>
            Date of Claim Submission
          </Typography>
        </div>
        <div className={classes.sectionBody}>
          <Grid
            container
            justify="flex-start"
            alignItems="flex-end"
            spacing={8}
          >
            <Grid
              item
              xs={12}
            >
              <Fields
                id="Date of Claim Submission From/To"
                names={[
                  'Date of Claim Submission From',
                  'Date of Claim Submission To',
                ]}
                component={DatepickerField}
                normalize={{
                  'Date of Claim Submission From': normalize.date,
                  'Date of Claim Submission To': normalize.date,
                }}
                fullWidth
                size="sm"
                minDate="01/01/1900"
                maxDate="01/01/2100"
                dateFormat={dateFormat}
                placeholder="mm/dd/yyyy"
                variant="range"
                startDateName="Date of Claim Submission From"
                endDateName="Date of Claim Submission To"
                datepickerPlacement={datepickerPlacement}
                rangeButtons
              />
            </Grid>
          </Grid>
        </div>
      </div>
    }
    <Grid
      container
      justify="space-between"
      alignItems="center"
      spacing={0}
      wrap="nowrap"
    >
      <Grid item>
        <Button
          className={classes.buttonCancel}
          color="inherit"
          onClick={onClear}
        >
          Clear
        </Button>
      </Grid>
      <Grid item>
        <Button
          className={classes.buttonSubmit}
          color="primary"
          onClick={() => { submit(); }}
        >
          Apply
        </Button>
      </Grid>
    </Grid>
  </Fragment>
);

DateRangeFieldsForm.propTypes = {
  submit: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  dateOfCreation: PropTypes.bool,
  dateOfCreationLabel: PropTypes.string,
  dateOfService: PropTypes.bool,
  dateOfDelivery: PropTypes.bool,
  datepickerPlacement: PropTypes.oneOf(['middle-left', 'middle-right']),
  classes: PropTypes.shape({
    section: PropTypes.string.isRequired,
    sectionHeader: PropTypes.string.isRequired,
    sectionBody: PropTypes.string.isRequired,
    gridDateRange: PropTypes.string.isRequired,
    buttonSubmit: PropTypes.string.isRequired,
    buttonCancel: PropTypes.string.isRequired,
  }).isRequired,
};

DateRangeFieldsForm.defaultProps = {
  dateOfCreation: true,
  dateOfService: true,
  dateOfDelivery: false,
  datepickerPlacement: 'middle-left',
  dateOfCreationLabel: 'Date of Creation',
};

export default reduxForm({
  onSubmit: values => values,
  validate: validateForm,
  enableReinitialize: true,
})(withStyles(styles)(DateRangeFieldsForm));



// WEBPACK FOOTER //
// ./src/components/fields/DateRangeFields/DateRangeFieldsForm.js